﻿import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertCircle, Wallet } from 'lucide-react';
import { PERMISSIONS } from '@pms/shared';
import { usePermission } from '@/hooks/usePermission';
import { Card, CardContent } from '@/components/ui/card';
import { SectionHeader } from '@/components/ui/section-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { financeService } from '@/services/finance.service';

interface FinanceWidgetProps {
  isAdminTier: boolean;
}

function formatCurrency(amount: number | null | undefined): string {
  if (amount == null) return '—';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

function AdminFinance() {
  const { has } = usePermission();

  const purchasesQ = useQuery({
    queryKey: ['dashboard', 'finance', 'purchases'],
    queryFn: () => financeService.listPurchases({ page: 1, limit: 5 }),
    enabled: has(PERMISSIONS.FINANCE_PURCHASES_VIEW),
  });

  const reimburseQ = useQuery({
    queryKey: ['dashboard', 'finance', 'pending-reimbursements'],
    queryFn: () => financeService.listAllReimbursementLedger({ page: 1, limit: 1 }),
    enabled: has(PERMISSIONS.FINANCE_REIMBURSEMENTS_MANAGE),
  });

  const purchases = purchasesQ.data?.data ?? [];

  return (
    <Card className="shadow-card">
      <SectionHeader
        icon={<Wallet />}
        title="Finance"
        action={
          <Link to="/finance/purchases" className="text-xs font-normal text-primary hover:underline">
            View All
          </Link>
        }
      />
      <CardContent className="space-y-4 p-4">
        {has(PERMISSIONS.FINANCE_REIMBURSEMENTS_MANAGE) && (
          <div className="flex items-center justify-between rounded-lg border bg-background/60 px-3 py-2">
            <p className="text-sm font-medium text-muted-foreground">Pending Reimbursements</p>
            <span className="text-sm font-semibold">
              {reimburseQ.isLoading ? '...' : (reimburseQ.data?.pagination?.total ?? 0)}
            </span>
          </div>
        )}

        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Recent Purchases
          </p>
          {purchasesQ.isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-10 animate-pulse rounded-lg bg-muted/50" />
              ))}
            </div>
          ) : purchasesQ.isError ? (
            <div className="flex flex-col items-center gap-2 py-4 text-center">
              <AlertCircle className="h-5 w-5 text-destructive" />
              <p className="text-sm text-muted-foreground">Failed to load</p>
              <Button size="sm" variant="outline" onClick={() => purchasesQ.refetch()}>Retry</Button>
            </div>
          ) : purchases.length === 0 ? (
            <p className="text-sm text-muted-foreground">No recent purchases</p>
          ) : (
            <div className="space-y-2">
              {purchases.map((p) => (
                <div key={p.id} className="flex items-center justify-between text-sm">
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{p.description ?? p.vendor_name ?? 'Purchase'}</p>
                    <p className="text-xs text-muted-foreground">{formatDate(p.created_at)}</p>
                  </div>
                  <span className="ml-2 shrink-0 font-semibold text-foreground">
                    {formatCurrency(Number(p.total_amount))}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function UserFinance() {
  const { has } = usePermission();

  const myReimburseQ = useQuery({
    queryKey: ['dashboard', 'finance', 'my-reimbursement'],
    queryFn: () => financeService.getMyReimbursement(),
    enabled: has(PERMISSIONS.FINANCE_REIMBURSEMENT_REQUEST),
  });

  const myLedgerQ = useQuery({
    queryKey: ['dashboard', 'finance', 'my-ledger'],
    queryFn: () => financeService.getMyReimbursementLedger({ page: 1, limit: 4 }),
    enabled: has(PERMISSIONS.FINANCE_REIMBURSEMENT_REQUEST),
  });

  if (!has(PERMISSIONS.FINANCE_REIMBURSEMENT_REQUEST)) return null;

  const balance = myReimburseQ.data?.account?.balance;
  const ledgerItems = myLedgerQ.data?.data ?? [];

  return (
    <Card className="shadow-card">
      <SectionHeader
        icon={<Wallet />}
        title="My Reimbursements"
        action={
          <Link to="/reimbursement" className="text-xs font-normal text-primary hover:underline">
            View All
          </Link>
        }
      />
      <CardContent className="space-y-4 p-4">
        {myReimburseQ.isLoading ? (
          <div className="h-14 animate-pulse rounded-xl bg-muted/50" />
        ) : (
          <div className="rounded-xl border bg-teal-50/50 px-4 py-3">
            <p className="text-xs text-muted-foreground">Available Balance</p>
            <p className="text-xl font-bold text-teal-700">{formatCurrency(balance !== undefined ? Number(balance) : undefined)}</p>
          </div>
        )}

        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Recent Transactions
          </p>
          {myLedgerQ.isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-9 animate-pulse rounded-lg bg-muted/50" />
              ))}
            </div>
          ) : ledgerItems.length === 0 ? (
            <p className="text-sm text-muted-foreground">No transactions yet</p>
          ) : (
            <div className="space-y-2">
              {ledgerItems.map((entry) => (
                <div key={entry.id} className="flex items-center justify-between text-sm">
                  <span className="truncate text-muted-foreground">
                    {entry.description ?? entry.category_name ?? 'Transaction'}
                  </span>
                  <div className="ml-2 flex shrink-0 items-center gap-1.5">
                    <span className={`font-semibold ${entry.entry_type === 'credit' ? 'text-emerald-700' : 'text-rose-700'}`}>
                      {entry.entry_type === 'credit' ? '+' : '-'}{formatCurrency(Math.abs(Number(entry.amount)))}
                    </span>
                    <Badge variant={entry.entry_type === 'credit' ? 'success' : 'destructive'}>
                      {entry.entry_type}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function FinanceWidget({ isAdminTier }: FinanceWidgetProps) {
  if (isAdminTier) return <AdminFinance />;
  return <UserFinance />;
}
